import { MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { chain, isEmpty } from 'lodash';
import { filter, takeUntil } from 'rxjs/operators';
import { Settings, UrlBuilder } from '../../../../core';
import { CultureInfo } from '../../../../core/culture-info';
import { CategoryNaturalKey } from '../../../../documents';
import { SiteCommunicationsMassUploadStorage } from '../../storages/site-communications-mass-upload-storage';
import { FileFormArray } from './../../../../covance-file-upload/file-form-array';
import { CustomMethodUploadOptions } from './../base/method-upload-options';
import { ExcelParser } from './excel-parser';
import { SiteSpecificUploadDialogBase } from './site-specific-upload-dialog-base';
import { UploadErrorDialogComponent, UPLOAD_ERROR_DIALOG_DATA_TOKEN } from './upload-error-dialog.component';
import { serializeDateOnly } from '../../../../core/serializers';

interface Metadata {
    letterNumber: string;
    title: string;
    siteLimitDate?: string;
    items: MetadataItem[];
}

interface MetadataItem {
    zcProjectNumber: string;
    siteNum: string;
}

interface ParsedRow {
    parseError?: string;
    item?: MetadataItem;
}

@Component({
    selector: 'app-site-communications-mass-upload',
    template: require('./site-communications-mass-upload.component.html'),
    styles: [require('./site-communications-mass-upload.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteCommunicationsMassUploadComponent extends SiteSpecificUploadDialogBase {
    public readonly letterNumberControl: FormControl;
    public readonly titleControl: FormControl;
    public readonly siteLimitDateControl: FormControl;
    public readonly recipientsFileFormArray: FileFormArray;

    public readonly allowedRecipientFileExtensions = ['.xlsx', '.xls'];
    public readonly allowedContentFilesExtensions: string[];

    public selectedRecepientsFile: File;

    private readonly categoryId: CategoryNaturalKey = 'site-communications';
    private readonly projectHeaderCellContent = 'Project';
    private readonly siteHeaderCellContent = 'Site';

    constructor(
        dialogService: MdlDialogService,
        private readonly siteCommunicationsMassUploadStorage: SiteCommunicationsMassUploadStorage,
        protected readonly settings: Settings,
        private excelParser: ExcelParser,
        private urlBuilder: UrlBuilder
    ) {
        super(null, dialogService, null, siteCommunicationsMassUploadStorage);

        this.errorDetails$
            .pipe(
                filter(errorDetails => !isEmpty(errorDetails)),
                takeUntil(this.onDialogDestroying)
            ).subscribe(errorDetails => this.displayErrors(errorDetails));

        this.letterNumberControl = new FormControl();
        this.titleControl = new FormControl();
        this.siteLimitDateControl = new FormControl();
        this.recipientsFileFormArray = new FileFormArray();
        this.allowedContentFilesExtensions = this.settings.uploadedFileExtensionsByCategoryId(this.categoryId);

        this.editorForm.addControl('letterNumber', this.letterNumberControl);
        this.editorForm.addControl('title', this.titleControl);
        this.editorForm.addControl('siteLimitDate', this.siteLimitDateControl);
        this.editorForm.addControl('recipientsFile', this.recipientsFileFormArray);
    }

    public onSelectedRecepientsFiles(selectedFiles: Map<File, CultureInfo>) {
        this.selectedRecepientsFile = selectedFiles.keys().next().value;
    }

    public async addFiles() {
        const sheetRows = await this.excelParser.parseFile(this.selectedRecepientsFile);

        const headerRow = sheetRows[0];
        const projectHeaderCell = headerRow[0];
        const siteHeaderCell = headerRow[1];
        if (!(projectHeaderCell === this.projectHeaderCellContent && siteHeaderCell === this.siteHeaderCellContent)) {
            this.displayErrors([`Invalid Header - The header should contain two columns - "${this.projectHeaderCellContent}" and "${this.siteHeaderCellContent}"`]);
            return;
        }

        const parsedRows = chain(sheetRows)
            .slice(1)
            .filter(row => !isEmpty(row))
            .map(row => this.parseSheetRow(row))
            .value();

        const invalidRows = parsedRows.filter(r => r.parseError);
        if (invalidRows.length > 0) {
            this.displayErrors(invalidRows.map(r => r.parseError));
            return;
        }

        if (isEmpty(parsedRows)) {
            this.displayErrors(['Invalid recepients file: it must contain a header and at least one data row']);
            return;
        }

        const metadataItems = parsedRows.map(r => r.item);

        const duplicates = this.getDuplicates(metadataItems);
        if (duplicates.length > 0) {
            this.displayErrors(duplicates.map(d => `${d} is a duplicate`));
            return;
        }

        const metadata: Metadata = {
            letterNumber: this.letterNumberControl.value,
            title: this.titleControl.value,
            siteLimitDate: serializeDateOnly(this.siteLimitDateControl.value),
            items: metadataItems
        };
        
        const confirmed = await this.showConfirm('Proceed?');

        if (confirmed) {
            this.upload(new CustomMethodUploadOptions(
                () => this.siteCommunicationsMassUploadStorage.create(
                    null,
                    this.selectedFiles,
                    metadata
                )
            ));
        }
    }

    public closePopup() {
        this.urlBuilder
            .esa()
            .globalPopup()
            .empty()
            .navigate({ preserveQueryParams: true });
    }

    private parseSheetRow(sheetRow: any[]): ParsedRow {
        if (sheetRow.length > 2) {
            return {
                parseError: `Row has extra column(s): ${sheetRow.join(' ')}`
            };
        }

        const projectCell = sheetRow[0];
        const zcProjectNumber = projectCell;
        if (isNaN(zcProjectNumber)) {
            return {
                parseError: `Invalid Project in row ${sheetRow.join(' ')}`
            };
        }

        let sitesCell = sheetRow[1];
        if (sitesCell == null || sitesCell === '') {
            return {
                parseError: `Invalid Sites in row ${sheetRow.join(' ')}`
            };
        }
        sitesCell = sitesCell.toString();
        const siteNum = sitesCell.toUpperCase() === 'ALL' ? 'ALL' : sitesCell;

        return {
            item: {
                zcProjectNumber,
                siteNum
            }
        };
    }

    private getDuplicates(items: MetadataItem[]): string[] {
        const uniqueItems = new Set<string>();
        const duplicates: string[] = [];

        for (let item of items) {
            const itemDescription = `${item.siteNum} - ${item.zcProjectNumber}`;
            if (uniqueItems.has(itemDescription)) {
                duplicates.push(itemDescription);
            } else {
                uniqueItems.add(itemDescription);
            }
        }

        return duplicates;
    }

    private displayErrors(errors: string[]) {
        this.dialogService.showCustomDialog({
            component: UploadErrorDialogComponent,
            providers: [{
                provide: UPLOAD_ERROR_DIALOG_DATA_TOKEN,
                useValue: {
                    title: 'Recipients file is invalid!',
                    errors: errors
                }
            }],
            styles: {
                'max-height': '90vh',
                'overflow': 'auto'
            },
            isModal: true,
            clickOutsideToClose: true
        });
    }
}
